import { ChartHopIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/charthop';
import styled from '@emotion/styled';
import { Button, Dialog, DialogContent, TextField } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { lightMdToJsx } from '../../../../../../services/formatter';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  masked
} from '../common';
import { removeChartHopConfig, useChartHopConfig } from './service';
const NAME = 'ChartHop (updated)';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const SetupDialog = ({
  config,
  open,
  onClose,
  onSuccessfulIntegration
}: IntegrationSetupDialogWithConfigProperties<ChartHopIntegrationConfig>) => {
  const { t } = useUnsafeTranslation();

  const { orgSlug, apiToken } = config?.data || { orgSlug: '', apiToken: '' };

  const [state, setState] = useState<{
    orgSlug: string;
    apiToken: string;
  }>({
    orgSlug,
    apiToken
  });

  useEffect(() => {
    setState({ orgSlug, apiToken: masked(apiToken) });
  }, [orgSlug, apiToken]);

  if (!config) {
    return null;
  }
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <FormRow>
          <CustomInputLabel label={t('Org Slug')} fullWidth>
            <TextField
              variant="outlined"
              value={state.orgSlug}
              onChange={(ev) =>
                setState((s) => ({ ...s, orgSlug: ev.target.value }))
              }
              fullWidth
              helperText={lightMdToJsx(
                t(
                  'Your org slug in e.g. app.charthop.com/my-company would be *my-company*'
                )
              )}
            />
          </CustomInputLabel>
        </FormRow>
        <FormRow>
          <CustomInputLabel label={t('API Token')} fullWidth>
            <TextField
              variant="outlined"
              value={state.apiToken}
              onChange={(ev) =>
                setState((s) => ({ ...s, apiToken: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeChartHopConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
            disabled={!orgSlug && !apiToken}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={() =>
                callFirebaseFunction(
                  'integrations_charthop-verifyCredentialsAndStore',
                  {
                    orgId: config.data.orgId,
                    orgSlug: state.orgSlug,
                    apiToken: state.apiToken
                  }
                ).then(onSuccessfulIntegration)
              }
              disabled={
                !state.orgSlug ||
                !state.apiToken ||
                state.orgSlug === config.data.orgSlug ||
                state.apiToken === config.data.apiToken
              }
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

export const ChartHopSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useChartHopConfig(org.id, userId);
  const cfg = config || null;

  return (
    <>
      <IntegrationConfigurationButton
        name={NAME}
        logoUrl="/images/integrations-logos/charthop.png"
        functionalities={functionalities}
        active={!!cfg?.data.active}
        interactive={interactive}
        renderSetupDialog={({
          open,
          onClose,
          onFailure,
          onSuccessfulIntegration
        }: IntegrationSetupDialogProperties) => (
          <SetupDialog
            config={cfg}
            open={open}
            onClose={onClose}
            onFailure={onFailure}
            onSuccessfulIntegration={onSuccessfulIntegration}
          />
        )}
      />
    </>
  );
};
